import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { AdvancedImage } from "@cloudinary/react";
import { fill } from "@cloudinary/url-gen/actions/resize";
import ProductService from "../../../../../../service/ProductService";
import { cloudinaryInstance } from "../../../../../../utilities/Cloudinary/cloudinaryInstance";
import { formatPrice } from "../../../../../../utilities/formatPrice";
import useCurrency from "../../../../../../hooks/useCurrency";
import localeText from "../../../../../../locale";
import { normalizeExtracoinUsagePercent } from "../../../../utils/extracoinUsagePercent";
import "./ExtracreditOpportunities.css";

const productService = new ProductService();

const SCROLL_AMOUNT = 280;

const ExtracreditOpportunities = ({ extracoinPercentage, currencySymbol }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const { currencySymbol: displayCurrencySymbol } = useCurrency(currencySymbol);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: direction * SCROLL_AMOUNT, behavior: "smooth" });
  };
  const pct = normalizeExtracoinUsagePercent(extracoinPercentage);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await productService.getMostOrderedProducts(10);
        if (!cancelled && (res.messageCode === 200000 || res.messageCode === 200001)) {
          setProducts(res.result || []);
        } else {
          setProducts([]);
        }
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="card border-round-md p-4 mb-6">
        <div className="flex justify-content-between align-items-center mb-3">
          <h4 className="font-semibold m-0">
            {localeText("opportunitiesToUse") || "Oportunidade de uso"}
          </h4>
        </div>
        <div className="flex gap-2 overflow-hidden">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="flex-shrink-0 border-round overflow-hidden"
              style={{ width: "200px", height: "260px", backgroundColor: "#F5F5F5" }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (products.length === 0) return null;

  return (
    <div className="mb-5">
      <div className="flex justify-content-between align-items-center mb-3">
        <div className="font-bold m-0" style={{ color: "var(--theme-text, #172B4D)" }}>
          {localeText("opportunitiesToUse") || "Oportunidades com Extracréditos"}
        </div>
        <Link
          to="/shop"
          className="font-medium no-underline flex align-items-center gap-2"
          style={{ color: "var(--theme-secondary, #F7B005)" }}
        >
          <i className="pi pi-external-link" />
          {localeText("accessStore") || "Acessar loja"}
        </Link>
      </div>
      <div className="relative" style={{ width: "100%" }}>
        <button
          type="button"
          onClick={() => scroll(-1)}
          className="extracredit-opportunities-nav-btn extracredit-opportunities-nav-left flex align-items-center justify-content-center border-round"
          style={{
            position: "absolute",
            left: 10,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            width: "40px",
            height: "40px",
            border: "1px solid var(--theme-border, #E9ECEF)",
            backgroundColor: "var(--theme-background, #fff)",
            color: "var(--theme-text, #344563)",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
          aria-label={localeText("previous") || "Anterior"}
        >
          <i className="pi pi-chevron-left" />
        </button>
        <button
          type="button"
          onClick={() => scroll(1)}
          className="extracredit-opportunities-nav-btn extracredit-opportunities-nav-right flex align-items-center justify-content-center border-round"
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            width: "40px",
            height: "40px",
            border: "1px solid var(--theme-border, #E9ECEF)",
            backgroundColor: "var(--theme-background, #fff)",
            color: "var(--theme-text, #344563)",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
          aria-label={localeText("next") || "Próximo"}
        >
          <i className="pi pi-chevron-right" />
        </button>
        <div
          ref={scrollRef}
          className="extracredit-opportunities-scroll flex gap-3 overflow-x-auto overflow-y-hidden"
          style={{
            scrollSnapType: "x mandatory",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {products.map((product) => {
            const productId = product.id ?? product.idproduct;
            const imageId = product.imageid1 ?? product.imageId ?? product.imageid;
            const title = product.title ?? product.name ?? "";
            const price = product.priceUnique ?? 0;
            const promotionalPrice = product.promotionalPriceUnique ?? 0;
            const seller = product.user?.username ?? product.seller?.username ?? "";
            const percentUse = product.percentExtraCoin ?? pct;

            return (
              <Link
                key={productId}
                to={`/product/${productId}`}
                className="flex-shrink-0 border-round-md overflow-hidden no-underline p-3 flex flex-column justify-content-between extracredits-opportunity-card"
                style={{
                  width: "250px",
                  scrollSnapAlign: "start",
                  color: "inherit",
                }}
              >
                <div>
                  <div className="relative overflow-hidden" style={{ height: "140px" }}>
                    {percentUse > 0 && (
                      <div
                        className="absolute px-2 py-1 border-round-md text-xs font-medium"
                        style={{
                          backgroundColor: "var(--theme-primary, #1DAF61)",
                          color: "#fff",
                          top: "6px",
                          left: "6px",
                        }}
                      >
                        {localeText("useCreditPercent")?.replace("{percent}", percentUse) ||
                          `Use ${percentUse}% Crédito`}
                      </div>
                    )}
                    {imageId ? (
                      <AdvancedImage
                        cldImg={cloudinaryInstance
                          .image(imageId)
                          .resize(fill().width(200).height(140))
                          .format("webp")}
                        alt={title}
                        className="border-round-md"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <div
                        className="flex align-items-center justify-content-center w-full h-full"
                        style={{ backgroundColor: "var(--theme-border, #E9ECEF)" }}
                      >
                        <i className="pi pi-image text-4xl text-400" />
                      </div>
                    )}
                  </div>
                  <div className="mt-2 mb-3">
                    <div
                      className="font-semibold text-xs mb-1 line-clamp-2"
                      style={{ color: "var(--theme-text, #344563)" }}
                    >
                      {title}
                    </div>
                    {seller && (
                      <div
                        className="text-xs mb-2"
                        style={{ color: "var(--theme-text-muted, #7A869A)" }}
                      >
                        {seller}
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className="flex flex-column gap-2 align-items-end"
                  style={{
                    backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                    padding: "8px 6px",
                    borderRadius: "6px",
                  }}
                >
                  <div
                    className={`flex align-items-center ${promotionalPrice > 0 ? "justify-content-between w-full" : ""}`}
                  >
                    <div
                      className={`text-sm ${promotionalPrice > 0 ? "line-through" : "font-bold"}`}
                      style={{
                        color:
                          promotionalPrice > 0
                            ? "var(--theme-text-muted, #7A869A)"
                            : "var(--theme-text, #344563)",
                      }}
                    >
                      {displayCurrencySymbol} {formatPrice(price)}
                    </div>
                    {promotionalPrice > 0 && (
                      <div
                        className="text-sm font-bold"
                        style={{ color: "var(--theme-text, #344563)" }}
                      >
                        {displayCurrencySymbol} {formatPrice(promotionalPrice)}
                      </div>
                    )}
                  </div>

                  {percentUse > 0 && (
                    <div className="text-xs" style={{ color: "var(--theme-primary, #1DAF61)" }}>
                      + {(price * (percentUse / 100)).toFixed(2)} ECR
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ExtracreditOpportunities;
